import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { Survey } from '@context/subscription-box/useSurvey'
import SurveyAuthModal from '@components/subscription-box/modals/SurveyAuthModal'
import AddToCart from '@components/prismic/AddToCart'
import { isCustomizableBoxVariant } from '@lib/utils/isSubscriptionBox'
import { formatPrice, getCurrencySymbol } from '@lib/utils/formatPrice'

type SubscriptionData = {
	pricePerBox: number
	compareAtPricePerBox: number | null
	pricePerMonth: number
	currencyCode: string
	sellingPlanId: string
	frequencyText: string
}

type ProductData = {
	price: string
	compareAtPrice?: string
	id: string
	name: string
	availableForSale: boolean
	requiresSellingPlan: boolean
	variants: { id: string; title?: string }[]
}

interface PricingCardProps {
	title: string
	chargeFrequency: string
	tag: string | null
	product: ProductData | undefined
	subscription?: SubscriptionData
	ctaText: string
}

export default function PricingCard({
	title,
	chargeFrequency,
	tag,
	product,
	subscription,
	ctaText
}: PricingCardProps) {
	const router = useRouter()
	const [loading, setLoading] = useState(false)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const { pricePerBox, compareAtPricePerBox, pricePerMonth } = subscription || {}

	// Calculate compareAtPricePerBox and total discount from product data
	// We always use product totals to ensure consistent discount calculation (applying full discount to first box)
	// This ensures the same behavior in both local and production environments, regardless of subscription data
	let calculatedCompareAtPricePerBox: number | null = null
	let calculatedTotalDiscount = 0
	
	if (product?.compareAtPrice && product?.price && pricePerBox) {
		const productPrice = parseFloat(product.price)
		const productCompareAtPrice = parseFloat(product.compareAtPrice)
		// Only calculate if compareAtPrice is actually higher than price (there's a discount)
		if (productPrice > 0 && productCompareAtPrice > productPrice) {
			// Calculate total discount (difference between compareAtPrice and price)
			const totalDiscount = productCompareAtPrice - productPrice
			
			// For prepaid plans, we need to calculate per-box prices
			// Determine how many boxes this payment covers (extract from product price vs pricePerBox)
			if (pricePerBox > 0) {
				const boxesInPayment = Math.round(productPrice / pricePerBox) || 1
				// CompareAtPrice per box = total compareAtPrice / number of boxes
				// This gives us the regular price per box (e.g., $264 / 12 = $22)
				calculatedCompareAtPricePerBox = productCompareAtPrice / boxesInPayment
				calculatedTotalDiscount = totalDiscount
			}
		}
	}
	
	// Fallback to subscription data if product calculation didn't run
	const displayCompareAtPrice = calculatedCompareAtPricePerBox ?? compareAtPricePerBox
	
	// For display: if we calculated a total discount, apply the entire discount to the first box
	// This shows the discount is on the first box only, not spread across all boxes
	// First box price = compareAtPricePerBox - totalDiscount (e.g., $22 - $6.25 = $15.75)
	const displayPricePerBox = (calculatedCompareAtPricePerBox && calculatedTotalDiscount > 0)
		? calculatedCompareAtPricePerBox - calculatedTotalDiscount  // First box gets entire discount
		: pricePerBox

	// Debug logging
	console.log('[PricingCard] Debug:', {
		title,
		pricePerBox,
		compareAtPricePerBox,
		calculatedCompareAtPricePerBox,
		productCompareAtPrice: product?.compareAtPrice,
		productPrice: product?.price,
		displayCompareAtPrice,
		displayPricePerBox
	})

	// Determine which price is higher/lower for display
	// Only show discount if compareAtPrice exists AND is actually higher than price
	const hasComparePrice = displayCompareAtPrice != null && displayPricePerBox != null && displayCompareAtPrice > displayPricePerBox
	let higherPrice: number | null = null
	let lowerPrice: number | null = displayPricePerBox || null
	let savingsAmount: number | null = null
	
	if (hasComparePrice && displayCompareAtPrice != null && displayPricePerBox != null) {
		higherPrice = displayCompareAtPrice
		lowerPrice = displayPricePerBox
		// Calculate savings as first-box only (difference between compareAt and price)
		// This makes it look like savings is just off the first box, not spread across all boxes
		savingsAmount = displayCompareAtPrice - displayPricePerBox
	}

	console.log('[PricingCard] Calculated values:', {
		hasComparePrice,
		higherPrice,
		lowerPrice,
		savingsAmount,
		willShowDiscount: hasComparePrice && higherPrice != null && lowerPrice != null && savingsAmount != null && savingsAmount > 0
	})

	// Generate charge frequency text - show first charge price and regular price for future charges if there's a discount
	const chargeFrequencyParts = React.useMemo(() => {
		if (hasComparePrice && higherPrice && lowerPrice && savingsAmount && savingsAmount > 0) {
			const currencySymbol = getCurrencySymbol()
			
			// Check if this is a prepaid plan (format: "Charges Every X months For $Y" or "Charges Every X months For $Y, Ships Every Month")
			const prepaidMatch = chargeFrequency.match(/Charges\s+Every\s+(\d+)\s+months?\s+For\s+[$]?([\d.]+)/i)
			
			if (prepaidMatch) {
				// Prepaid plan with discount - show pricing in simplified format
				const monthsCount = parseInt(prepaidMatch[1], 10) // e.g., 3 from "Every 3 months"
				
				// For prepaid, use product price and compareAtPrice totals directly
				// First charge: current product price (already discounted)
				// Then price: product compareAtPrice (regular price)
				const productPrice = product?.price ? parseFloat(product.price) : null
				const productCompareAtPrice = product?.compareAtPrice ? parseFloat(product.compareAtPrice) : null
				
				// Fallback: calculate from per-box prices if product totals not available
				const discountedTotal = productPrice ?? ((lowerPrice || 0) * monthsCount)
				const regularTotal = productCompareAtPrice ?? ((higherPrice || 0) * monthsCount)
				
				// Extract the "Ships Every" part if it exists, but remove any pricing info
				const shipsMatch = chargeFrequency.match(/,?\s*(Ships.*)$/i)
				let shipsPart = shipsMatch ? shipsMatch[1] : ''
				// Remove any "For $X" from the ships part since we already show pricing
				shipsPart = shipsPart.replace(/\s+For\s+[$]?[\d.]+/i, '')
				
				// New format: "$257.75 then $264 every 12 months. Ships Every Month"
				const baseText = `${currencySymbol}${formatPrice(discountedTotal.toString())} then ${currencySymbol}${formatPrice(regularTotal.toString())} every ${monthsCount} months`
				return { baseText, shipsPart }
			}
			
			// Monthly plan (format: "Charges And Ships Every Month For $X" or similar)
			// Show first month discount, then regular price
			const firstMonthPrice = lowerPrice
			const regularMonthPrice = higherPrice
			
			// Extract the "Ships" part to preserve shipping frequency, but remove pricing info
			const shipsMatch = chargeFrequency.match(/,?\s*(Ships.*)$/i) || chargeFrequency.match(/\s+(Ships.*)$/i) || chargeFrequency.match(/And\s+(Ships.*)$/i)
			let shipsPart = shipsMatch ? shipsMatch[1] : ''
			// Remove any "For $X" from the ships part since we already show pricing
			shipsPart = shipsPart.replace(/\s+For\s+[$]?[\d.]+/i, '')
			
			const baseText = `Charges ${currencySymbol}${formatPrice(firstMonthPrice.toString())} for the first month, then ${currencySymbol}${formatPrice(regularMonthPrice.toString())} per month`
			return { baseText, shipsPart }
		}
		// No discount, return original charge frequency text
		return { baseText: chargeFrequency, shipsPart: null }
	}, [hasComparePrice, higherPrice, lowerPrice, savingsAmount, chargeFrequency, product?.price, product?.compareAtPrice])

	// Open the survey modal when the user adds a customizable box to the cart only if there is not cookie for bbSurveySubmitted
	const handleAddToCart = () => {
		const hasModalCookie = document.cookie
			.split(';')
			.some((cookie) => cookie.trim().startsWith('bbSurveySubmitted='))
		if (!hasModalCookie) {
			setIsModalOpen(true)
		}
	}

	return (
		<div className="bg-white h-full flex flex-col md:flex-row items-center justify-between p-6 md:p-10 relative">
			{/* Tag in top right corner */}
			{/* A11Y: Spark color border on all sides, text-gray-700 on white for 6:1 contrast ratio */}
			{tag && (
				<span className="absolute top-0 right-0 text-xs lg:text-base font-semibold text-white bg-bbx-spark tracking-wide px-2 py-1 rounded-none">
					{tag}
				</span>
			)}
			<div className="flex flex-col w-full justify-between h-full ">
				<div className="flex items-center mb-3">
					<span className="text-2xl md:text-3xl font-normal font-serif">{title}</span>
				</div>
				<div className="text-base font-light list-item-clock w-full">
					{chargeFrequencyParts.shipsPart ? (
						<>
							{chargeFrequencyParts.baseText}. <span className="font-bold">{chargeFrequencyParts.shipsPart}</span>
						</>
					) : (
						chargeFrequencyParts.baseText
					)}
				</div>
			</div>
			<div
				className={`flex md:items-end flex-row-reverse md:flex-col md:justify-items-end md:pl-7 mt-5 md:mt-0 justify-between gap-5 w-full ${!hasComparePrice ? 'items-center' : ''}`}
			>
				<div className="flex flex-col items-end justify-end h-full gap-2">
					{/* Show both prices when compareAtPrice exists */}
					{hasComparePrice && higherPrice != null && lowerPrice != null && savingsAmount != null && savingsAmount > 0 && (
						<>
							{/* Top row: SALE badge */}
							{/* {savingsAmount && savingsAmount > 0 && (
								<div className="flex justify-end w-full">
									<span className="text-bbx-red text-base md:text-lg font-bold uppercase tracking-wide whitespace-nowrap">
										SALE: {getCurrencySymbol()}{formatPrice(savingsAmount.toString())} OFF
									</span>
								</div>
							)} */}
							{/* Bottom row: Crossed out price on left, discounted price on right */}
							<div className="flex flex-col md:flex-row items-end md:items-baseline gap-0.5 md:gap-2">
								<div className="flex flex-row items-baseline gap-2">
									{/* A11Y: Changed from text-gray-400 to text-gray-500 for contrast compliance */}
									<span className="text-base md:text-lg text-gray-500 line-through">
										{getCurrencySymbol()}
										{formatPrice(higherPrice.toString())}
									</span>
									<span className="text-2xl font-bold text-bbx-red">
										{getCurrencySymbol()}
										{formatPrice(lowerPrice.toString())}
									</span>
								</div>
								<span className="text-base font-medium whitespace-nowrap text-right md:text-left">/ First Box</span>
							</div>
						</>
					)}
					{!hasComparePrice && lowerPrice && (
						/* No compareAtPrice - just show the price */
						<div className="flex flex-row items-baseline w-full h-full">
							<span className="text-2xl font-bold">
								{getCurrencySymbol()}
								{formatPrice(lowerPrice.toString())}
							</span>
							<span className="text-base font-medium whitespace-nowrap">/ Per Box</span>
						</div>
					)}
				</div>
				{product && subscription && product.variants && product.variants.length > 0 && (
					<AddToCart
						product={{
							id: product.id,
							name: product.name,
							price: {
								amount: (subscription.pricePerMonth || 0).toString(),
								currencyCode: subscription.currencyCode
							},
							vendor: 'birchbox',
							availableForSale: product.availableForSale,
							requiresSellingPlan: product.requiresSellingPlan
						}}
						variant={{
							id: product.variants[0].id,
							availableForSale: product.availableForSale,
							price: {
								amount: (subscription.pricePerMonth || 0).toString(),
								currencyCode: subscription.currencyCode
							}
						}}
						loading={loading}
						setLoading={setLoading}
						sellingPlanId={subscription.sellingPlanId}
						ctaText={ctaText}
						onAddToCart={handleAddToCart}
						className="flex button button-dark-blue md:mb-0 px-4 md:px-10 min-w-fit py-2 md:py-4 text-sm md:text-base"
					/>
				)}
			</div>

			{product &&
				product.variants &&
				product.variants.length > 0 &&
				isCustomizableBoxVariant(product.variants[0].id) && (
					<Survey surveyType="new">
						<SurveyAuthModal
							isModalOpen={isModalOpen}
							closeModal={() => {
								setIsModalOpen(false)
							}}
						/>
					</Survey>
				)}
		</div>
	)
}
